import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { describeError, Empty, Loading } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Field, Select } from "@/components/ui/input";
import { Table, Td, Th } from "@/components/ui/table";
import { api } from "@/lib/api";
import { qk, useGroups, useMe, useOrgQuery, useRoles } from "@/lib/hooks";
import type { BindingRow, RobotView, ScopeType, UserView } from "@/lib/types";
import { zh } from "@/i18n/zh";

export const roleLabel = (name: string) => zh.roles[name] ?? name;

export function BindingsDialog({ user, onClose }: { user: UserView; onClose: () => void }) {
  const qc = useQueryClient();
  const org = useOrgQuery();
  const roles = useRoles();
  const groups = useGroups();
  const robots = useQuery({
    queryKey: qk.robots({ ...org, picker: true }),
    queryFn: () => api<RobotView[]>("/v1/robots", { query: org }),
  });
  const list = useQuery({
    queryKey: qk.bindings(user.id),
    queryFn: () => api<BindingRow[]>(`/v1/users/${user.id}/bindings`),
  });
  const [roleId, setRoleId] = useState("");
  const [scope, setScope] = useState<Exclude<ScopeType, "platform">>("org");
  const [target, setTarget] = useState("");
  const [error, setError] = useState<string | null>(null);

  const me = useMe().data;
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.bindings(user.id) });
    // 改的是自己：菜单和按钮跟着变
    if (me?.user.id === user.id) {
      void qc.invalidateQueries({ queryKey: qk.me });
      void qc.invalidateQueries({ queryKey: ["robots"] });
    }
  };
  const add = useMutation({
    mutationFn: () =>
      api<BindingRow>(`/v1/users/${user.id}/bindings`, {
        method: "POST",
        body: { role_id: roleId, scope_type: scope, scope_id: scope === "org" ? user.org_id : target || null },
      }),
    onSuccess: () => {
      setError(null);
      setTarget("");
      invalidate();
    },
    onError: (e) => setError(describeError(e)),
  });
  const remove = useMutation({
    mutationFn: (bid: string) => api(`/v1/users/${user.id}/bindings/${bid}`, { method: "DELETE" }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError(describeError(e)),
  });

  const scopeName = (b: BindingRow) => {
    if (b.scope_type === "org") return zh.bindings.scopeType.org;
    if (b.scope_type === "robot_group") return groups.data?.find((g) => g.id === b.scope_id)?.name ?? b.scope_id;
    if (b.scope_type === "robot") return robots.data?.find((r) => r.id === b.scope_id)?.name ?? b.scope_id;
    return zh.bindings.scopeType[b.scope_type];
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={zh.bindings.title(user.display_name)} className="max-w-2xl">
      {list.isPending ? (
        <Loading />
      ) : list.data?.length ? (
        <Table>
          <thead>
            <tr>
              <Th>{zh.bindings.role}</Th>
              <Th>{zh.bindings.scope}</Th>
              <Th>{zh.bindings.target}</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {list.data.map((b) => (
              <tr key={b.id} data-testid="binding-row">
                <Td>{roleLabel(b.role_name)}</Td>
                <Td>{zh.bindings.scopeType[b.scope_type] ?? b.scope_type}</Td>
                <Td>{scopeName(b)}</Td>
                <Td className="text-right">
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={zh.bindings.remove}
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(b.id)}
                  >
                    <Trash2 />
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <Empty text={zh.bindings.none} />
      )}
      <form
        className="grid gap-3 border-t pt-4 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          add.mutate();
        }}
      >
        <Field label={zh.bindings.role}>
          <Select value={roleId} onChange={(e) => setRoleId(e.target.value)} required className="w-full" name="role">
            <option value="">—</option>
            {/* 平台角色不能通过机构接口授予（服务端 403） */}
            {roles.data?.filter((r) => r.name !== "platform_admin").map((r) => (
              <option key={r.id} value={r.id}>
                {roleLabel(r.name)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={zh.bindings.scope}>
          <Select
            value={scope}
            onChange={(e) => {
              setScope(e.target.value as typeof scope);
              setTarget("");
            }}
            className="w-full"
            name="scope"
          >
            <option value="org">{zh.bindings.scopeType.org}</option>
            <option value="robot_group">{zh.bindings.scopeType.robot_group}</option>
            <option value="robot">{zh.bindings.scopeType.robot}</option>
          </Select>
        </Field>
        <Field label={zh.bindings.target}>
          <Select
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            disabled={scope === "org"}
            required={scope !== "org"}
            className="w-full"
            name="target"
          >
            <option value="">—</option>
            {scope === "robot_group"
              ? groups.data?.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))
              : scope === "robot"
                ? robots.data?.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}（{r.sn}）
                    </option>
                  ))
                : null}
          </Select>
        </Field>
        <Button type="submit" disabled={add.isPending || !roleId}>
          {zh.bindings.add}
        </Button>
      </form>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </Dialog>
  );
}
